import { useCallback, useEffect, useState } from "react";
import Cookies from "js-cookie";

import { API_GET_ALL_PROJECTS_PROJECTID_AND_PROJECTNAME } from "../../../config/Endpoints.js";
import type { SelectOption } from "../../components/ui/index.ts";
import { getApi } from "../../utils/api.ts";

type ProjectOptionRow = {
  projectId: number | string;
  projectName: string;
};

type ProjectOptionsResponse = {
  data?: ProjectOptionRow[];
};

function unwrapProjects(raw: unknown): ProjectOptionRow[] {
  if (Array.isArray(raw)) return raw as ProjectOptionRow[];
  if (
    raw &&
    typeof raw === "object" &&
    Array.isArray((raw as ProjectOptionsResponse).data)
  ) {
    return (raw as ProjectOptionsResponse).data ?? [];
  }
  return [];
}

export function useProjectOptions() {
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setError(null);

    const accessToken = Cookies.get("accessToken");

    try {
      const response = await getApi<unknown>(
        API_GET_ALL_PROJECTS_PROJECTID_AND_PROJECTNAME,
        accessToken,
      );
      const projects = unwrapProjects(response.data);

      setOptions(
        projects.map((project) => ({
          value: String(project.projectId),
          label:
            project.projectName?.trim() || `Project ${project.projectId}`,
        })),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load projects");
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchProjects();
  }, [fetchProjects]);

  return { options, loading, error, refetch: fetchProjects };
}
